import { Platform } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import { ensureFreshAccessToken } from '../../shared/supabase/supabaseClient'
import { invokeEdgeOrPresent } from '../../shared/supabase/invokeEdgeOrPresent'
import { messageFromInvoke, throwIfInvokeError } from '../../shared/supabase/invokeErrorUtils'
import type { InvokeErr } from '../../shared/supabase/invokeErrorUtils';
import {
  isUnexpectedFailureHandled,
  markUnexpectedFailureHandled,
} from '../../shared/sentry/presentUnexpectedFailure';
import { invalidateStorefrontMpStatus } from '../../settings/infra/storefrontStatusCache';

WebBrowser.maybeCompleteAuthSession();

export const MERCADOPAGO_OAUTH_RETURN_URL_NATIVE = 'vuelto://mp-oauth/callback';

/** Shell route a restaurar tras OAuth web (misma ventana, no popup). */
export type MercadoPagoOAuthReturnShellRoute = 'BranchSettings' | 'MercadoPagoBranchSettings';

export const MP_OAUTH_RETURN_SHELL_ROUTE_KEY = 'vuelto_mp_oauth_return_shell_route';
export const MP_OAUTH_RETURN_BRANCH_ID_KEY = 'vuelto_mp_oauth_return_branch_id';
export const MP_OAUTH_RELOAD_FLAG = 'vuelto_mp_oauth_reload';

const DEFAULT_WEB_APP_ORIGIN = 'https://app.vuelto.ar';

function persistMercadoPagoOAuthReturnContext(
  branchId: string,
  returnShellRoute: MercadoPagoOAuthReturnShellRoute
): void {
  if (Platform.OS !== 'web' || typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.setItem(MP_OAUTH_RETURN_SHELL_ROUTE_KEY, returnShellRoute);
    sessionStorage.setItem(MP_OAUTH_RETURN_BRANCH_ID_KEY, branchId);
  } catch {
    /* ignore */
  }
}

/** URL de retorno post-OAuth: deep link nativo o callback HTTPS en la PWA. */
export function resolveMercadoPagoOAuthReturnUrl(): string {
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    const origin =
      process.env.EXPO_PUBLIC_EXPO_WEB_APP_ORIGIN?.trim().replace(/\/$/, '') || DEFAULT_WEB_APP_ORIGIN;
    return `${origin}/mp-oauth/callback`;
  }
  return MERCADOPAGO_OAUTH_RETURN_URL_NATIVE;
}

/** Bearer explícito + token fresco: evita 401 con JWT ES256 y con sesión casi vencida (p. ej. web). */
async function invokeWithUserJwt<T>(
  fn: string,
  body: Record<string, unknown>,
  present: { op: string; fallbackMessage: string },
): Promise<{ data: T | null; error: InvokeErr; presented: boolean }> {
  const token = await ensureFreshAccessToken();
  if (!token) {
    return {
      data: null,
      error: new Error('Sesión requerida. Iniciá sesión de nuevo.'),
      presented: false,
    };
  }
  const result = await invokeEdgeOrPresent<T>(
    fn,
    { body, headers: { Authorization: `Bearer ${token}` } },
    {
      module: 'mercadopago',
      op: present.op,
      fallbackMessage: present.fallbackMessage,
    },
  );
  return { data: result.data, error: result.error, presented: result.presented };
}

function throwAfterInvoke(
  data: unknown,
  error: InvokeErr,
  presented: boolean,
  fallback: string,
): never {
  if (presented || isUnexpectedFailureHandled(error)) {
    if (error instanceof Error && isUnexpectedFailureHandled(error)) throw error;
    throw markUnexpectedFailureHandled(new Error(messageFromInvoke(data, error, fallback)));
  }
  throw new Error(messageFromInvoke(data, error, fallback));
}

export async function requestMercadoPagoOAuthStart(branchId: string): Promise<string> {
  const appReturnUrl = resolveMercadoPagoOAuthReturnUrl();
  const { data, error, presented } = await invokeWithUserJwt<{ authorization_url?: string }>(
    'mercadopago-oauth-start',
    {
      branch_id: branchId,
      app_return_url: appReturnUrl,
    },
    { op: 'mp.oauth_start', fallbackMessage: 'No se pudo abrir Mercado Pago' },
  );
  if (error) throwAfterInvoke(data, error, presented, 'No se pudo abrir Mercado Pago');
  throwIfInvokeError(data);
  const d = data as { authorization_url?: string };
  if (!d?.authorization_url) throw new Error('Mercado Pago no respondió bien');
  return d.authorization_url;
}

export type MercadoPagoPosListItem = {
  store_id: string;
  store_name: string | null;
  store_external_id: string | null;
  external_pos_id: string;
  pos_name: string | null;
  pos_internal_id: string | null;
};

export type MercadoPagoPosPendingItem = {
  store_id: string | null;
  store_name: string | null;
  pos_name: string | null;
  pos_internal_id: string;
  reason: 'missing_external_id';
};

export type MercadoPagoPosListMeta = {
  mp_user_id?: string;
  stores_count?: number;
  pos_total?: number;
  pos_with_external_id?: number;
  pos_pending_link?: number;
};

export type MercadoPagoPosListResult = {
  items: MercadoPagoPosListItem[];
  pending_items: MercadoPagoPosPendingItem[];
  meta?: MercadoPagoPosListMeta;
  linkedExternalPosId?: string;
};

export async function fetchMercadoPagoPosList(branchId: string): Promise<MercadoPagoPosListResult> {
  const { data, error, presented } = await invokeWithUserJwt<{
    ok?: boolean;
    items?: MercadoPagoPosListItem[];
    pending_items?: MercadoPagoPosPendingItem[];
    meta?: MercadoPagoPosListMeta;
  }>(
    'mercadopago-pos-list',
    { branch_id: branchId },
    { op: 'mp.pos_list', fallbackMessage: 'Error al listar cajas' },
  );
  if (error) throwAfterInvoke(data, error, presented, 'Error al listar cajas');
  throwIfInvokeError(data);
  const d = data as {
    ok?: boolean;
    items?: MercadoPagoPosListItem[];
    pending_items?: MercadoPagoPosPendingItem[];
    meta?: MercadoPagoPosListMeta;
  };
  return {
    items: d.items ?? [],
    pending_items: d.pending_items ?? [],
    meta: d.meta,
  };
}

export async function linkMercadoPagoPosForBranch(
  branchId: string,
  posInternalId: string
): Promise<MercadoPagoPosListResult> {
  const { data, error, presented } = await invokeWithUserJwt<{
    ok?: boolean;
    items?: MercadoPagoPosListItem[];
    pending_items?: MercadoPagoPosPendingItem[];
    meta?: MercadoPagoPosListMeta;
    external_pos_id?: string;
  }>(
    'mercadopago-pos-list',
    {
      branch_id: branchId,
      link_pos_internal_id: posInternalId,
    },
    { op: 'mp.pos_link', fallbackMessage: 'Error al vincular la caja' },
  );
  if (error) throwAfterInvoke(data, error, presented, 'Error al vincular la caja');
  throwIfInvokeError(data);
  const d = data as {
    items?: MercadoPagoPosListItem[];
    pending_items?: MercadoPagoPosPendingItem[];
    meta?: MercadoPagoPosListMeta;
    linked_external_pos_id?: string;
  };
  const linkedExternalPosId = d.linked_external_pos_id?.trim() || undefined;
  return {
    items: d.items ?? [],
    pending_items: d.pending_items ?? [],
    meta: d.meta,
    linkedExternalPosId,
  };
}

export async function disconnectMercadoPago(branchId: string): Promise<{ ok: boolean }> {
  const { data, error, presented } = await invokeWithUserJwt<{ ok?: boolean }>(
    'mercadopago-disconnect',
    { branch_id: branchId },
    { op: 'mp.disconnect', fallbackMessage: 'No se pudo desvincular Mercado Pago' },
  );
  if (error) throwAfterInvoke(data, error, presented, 'No se pudo desvincular Mercado Pago');
  throwIfInvokeError(data);
  invalidateStorefrontMpStatus(branchId);
  return { ok: Boolean((data as { ok?: boolean })?.ok) };
}

export type MercadoPagoOAuthBrowserResult =
  | WebBrowser.WebBrowserAuthSessionResult
  | { type: 'redirect' };

/**
 * Abre el navegador de OAuth MP; al terminar, el callback del servidor redirige a la URL de retorno configurada.
 * En web redirige la ventana completa (popup rompe COOP y deja al usuario en el POS en otra pestaña).
 */
export async function openMercadoPagoOAuthBrowser(
  branchId: string,
  options?: { returnShellRoute?: MercadoPagoOAuthReturnShellRoute }
): Promise<MercadoPagoOAuthBrowserResult> {
  const returnUrl = resolveMercadoPagoOAuthReturnUrl();
  const url = await requestMercadoPagoOAuthStart(branchId);
  const returnShellRoute = options?.returnShellRoute ?? 'BranchSettings';

  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    persistMercadoPagoOAuthReturnContext(branchId, returnShellRoute);
    window.location.assign(url);
    return { type: 'redirect' };
  }

  return WebBrowser.openAuthSessionAsync(url, returnUrl, {
    preferEphemeralSession: false,
    showInRecents: true,
    ...(Platform.OS === 'android' && { createTask: true }),
  });
}
